// src/pages/league-dashboard/salary-contract/tabs/PreviewTab.js
import React, { useState } from 'react';
import { BarChart3, Edit, Users, Search, AlertCircle } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';
import { DynastyTable } from '../../../../services/tableService';
import PriceDistributionSummary from '../components/PriceDistributionSummary';
import PriceEditor from '../components/PriceEditor';
import { isRawPitcherPosition, RAW_PITCHER_POSITIONS } from '../../../../utils/playerClassification';

// Pure price filter — extracted from inner closure per Bible §14
// frontend testing convention so unit tests can target the filter
// rules directly. Filters by viewMode (hitting vs pitching), then
// position (or 'all'), then search term against full_name + mlb_team.
// Pitcher positions: SP, RP, CP, P. Returns a new array; never mutates.
export const getFilteredPrices = (prices, viewMode, positionFilter, searchFilter) => {
  if (!prices) return [];

  let filtered = prices;

  if (viewMode === 'hitting') {
    filtered = filtered.filter(p => !isRawPitcherPosition(p?.info?.position));
  } else {
    filtered = filtered.filter(p => isRawPitcherPosition(p?.info?.position));
  }

  if (positionFilter !== 'all') {
    filtered = filtered.filter(p => p?.info?.position === positionFilter);
  }

  if (searchFilter) {
    filtered = filtered.filter(p =>
      p?.info?.full_name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
      p?.info?.mlb_team?.toLowerCase().includes(searchFilter.toLowerCase())
    );
  }

  return filtered;
};

const PreviewTab = ({
  generatedPrices,
  setGeneratedPrices,
  customPrices,
  settings,
  league,
  leagueId,
  bulkEditMode,
  setBulkEditMode,
  selectedPlayers,
  setSelectedPlayers,
  setPriceAdjustmentModal
}) => {
  const [searchFilter, setSearchFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [viewMode, setViewMode] = useState('hitting');

  const positions = {
    hitting: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'UTIL'],
    pitching: [...RAW_PITCHER_POSITIONS]
  };

  const handleManualPriceChange = (playerId, newPrice) => {
    const prices = [...(generatedPrices?.prices || [])];
    const playerIndex = prices.findIndex(p =>
      p?.ids?.mlb === playerId
    );

    if (playerIndex >= 0) {
      const priceValue = parseFloat(newPrice);
      const rounded = Math.round(priceValue / settings.salary_increment) * settings.salary_increment;
      const finalPrice = Math.max(settings.min_salary, rounded);

      const pr = prices[playerIndex].pricing || {};
      const originalBaseline = pr._originalPrice || pr.baseline_price;

      // _originalPrice is ephemeral client-side state captured on
      // first manual edit so the "Reset to original" button can
      // restore the pre-edit baseline. It is NEVER persisted to the
      // backend — the save path discards it. Camelcase + underscore
      // prefix signals this is internal React state, not a backend
      // contract field.
      prices[playerIndex] = {
        ...prices[playerIndex],
        pricing: {
          ...pr,
          _originalPrice: originalBaseline,
          manual_price: finalPrice,
          baseline_price: finalPrice
        }
      };

      setGeneratedPrices({
        ...generatedPrices,
        prices: prices
      });

      // Recalculate summary stats locally
      const totalCost = prices.reduce((sum, p) => sum + (p.pricing?.baseline_price || 0), 0);
      const avgPrice = totalCost / prices.length;
      const maxPrice = Math.max(...prices.map(p => p.pricing?.baseline_price || 0));
      const minPrice = Math.min(...prices.map(p => p.pricing?.baseline_price || 0));
      const avgTeamSpend = totalCost / (league?.league_settings?.max_teams || 12);
      const capUsagePercent = settings.draft_cap ? Math.round((avgTeamSpend / settings.draft_cap) * 100) : 0;

      const over30 = prices.filter(p => (p.pricing?.baseline_price || 0) >= 30).length;
      const newSummary = {
        totalCost: Math.round(totalCost),
        avg_price: Math.round(avgPrice),
        max_price: Math.round(maxPrice),
        min_price: Math.round(minPrice),
        avgTeamSpend: Math.round(avgTeamSpend),
        capUsagePercent,
        distribution: { over_30: over30 }
      };

      setGeneratedPrices(prev => ({
        ...prev,
        summary: newSummary
      }));
    }
  };

  // Closure wrapper: binds current state slices to the named-export
  // helper. Recreated each render (matches prior closure behavior).
  const filterPricesFn = () =>
    getFilteredPrices(generatedPrices?.prices, viewMode, positionFilter, searchFilter);

  return (
    <div className="space-y-6">
      {/* Summary Stats with Charts */}
      {generatedPrices?.summary && (
        <PriceDistributionSummary 
          generatedPrices={generatedPrices}
          league={league}
          leagueId={leagueId}
        />
      )}

      {/* Player Price Table */}
      {generatedPrices?.prices && generatedPrices.prices.length > 0 ? (
        <PriceEditor
          generatedPrices={generatedPrices}
          customPrices={customPrices}
          settings={settings}
          viewMode={viewMode}
          setViewMode={setViewMode}
          positionFilter={positionFilter}
          setPositionFilter={setPositionFilter}
          searchFilter={searchFilter}
          setSearchFilter={setSearchFilter}
          bulkEditMode={bulkEditMode}
          setBulkEditMode={setBulkEditMode}
          selectedPlayers={selectedPlayers}
          setSelectedPlayers={setSelectedPlayers}
          setPriceAdjustmentModal={setPriceAdjustmentModal}
          handleManualPriceChange={handleManualPriceChange}
          getFilteredPrices={filterPricesFn}
          positions={positions}
          leagueId={leagueId}
          league={league}
        />
      ) : (
        <div className={dynastyTheme.components.card.base}>
          <div className="p-6 text-center py-12">
            <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.neutralLight}`} />
            <p className={dynastyTheme.classes.text.neutralLight}>
              No prices generated yet. Go to the Pricing tab to generate or upload prices.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewTab;